import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { getDataGridStyles } from "@/theme";
import { TranscriptSectionIndexDB } from "../../classes/audioDb";
import LocalRecordingActions from "./LocalRecordingActions";
import InfoCircleIcon from "@icons/info-circle.svg?react";
import HelpCircleIcon from "@icons/help-circle.svg?react";
import { Tooltip } from "react-tooltip";
import { VITE_PUBLIC_ENV } from "@/common/utils/constants";
import { PatientRecord } from "@/common/utils/types";

interface IncompleteUploadsProps {
    incompleteUploads: TranscriptSectionIndexDB[];
    handleAddTestUpload: () => void;
    handleUpload: (recording: TranscriptSectionIndexDB, existingPatientRecord: PatientRecord | null) => Promise<void>;
    handleDownload: (recording: TranscriptSectionIndexDB) => void;
    handleDelete: (recording: TranscriptSectionIndexDB) => Promise<void>;
}

const IncompleteUploads: React.FC<IncompleteUploadsProps> = ({
    incompleteUploads,
    handleAddTestUpload,
    handleUpload,
    handleDownload,
    handleDelete,
}) => {
    const columns: GridColDef[] = [
        {
            field: "patientName",
            headerName: "CLIPS",
            flex: 3,
            editable: false,
            sortable: false,
            disableColumnMenu: true,
            headerAlign: "left",
            align: "left",
            headerClassName: "datagrid-table--header",
            cellClassName: "datagrid-table--column-cell",
            renderCell: (params) => (
                <div className="flex flex-col mv-2">
                    <span className="text-sm text-gray-900 font-medium">{params.row.patientName || "-"}</span>
                    <span className="text-sm text-gray-600">{params.row.fileName || "file name"}</span>
                </div>
            ),
        },
        {
            field: "actions",
            type: "actions",
            headerName: "ACTIONS",
            flex: 1,
            headerAlign: "right",
            align: "right",
            sortable: false,
            disableColumnMenu: true,
            headerClassName: "datagrid-table--header",
            renderCell: (params) => {
                return (
                    <LocalRecordingActions
                        recording={params.row}
                        separateRetryButton={true}
                        showDeleteNow={false}
                        handleUpload={handleUpload}
                        handleDownload={handleDownload}
                        handleDelete={handleDelete}
                    />
                );
            },
        },
    ];

    return (
        <>
            <div>
                <div className="w-full flex items-center justify-between">
                    <div className="flex gap-2 items-baseline">
                        <h3 className="text-md font-semibold text-gray-900">Locally Stored Clips</h3>
                        <Tooltip
                            content="This table shows audio clips not fully uploaded. Audio clips are automatically deleted after one week (earlier if space is needed)."
                            anchorSelect="#incomplete-uploads-tooltip"
                            style={{ maxWidth: "300px", zIndex: 9999 }}
                        />
                        <InfoCircleIcon
                            id="incomplete-uploads-tooltip"
                            className={`h-[13px] w-[13px] mr-1 mt-1.5 focused `}
                        />
                    </div>

                    <div className="flex gap-2">
                        {VITE_PUBLIC_ENV !== "production" && (
                            <button
                                onClick={handleAddTestUpload}
                                className="flex items-center py-1 px-2 border rounded-md bg-blue-500 text-white shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                            >
                                <span className="text-xs">Add Test</span>
                            </button>
                        )}
                        <button
                            onClick={() => {
                                window.open(
                                    "https://help.gohappydoc.com/en/articles/8563272-trouble-shooting-common-issues",
                                    "_blank",
                                    "noopener,noreferrer",
                                );
                            }}
                            className="flex items-center py-1 px-2 border rounded-md bg-white shadow-sm text-gray-900 focus:outline-none focused"
                        >
                            <HelpCircleIcon className={`h-[13px] w-[13px] mr-1`} />
                            <span className="text-xs">Help</span>
                        </button>
                    </div>
                </div>
            </div>
            <div className="flex flex-col w-full h-full max-h-[400px]">
                <DataGrid
                    getRowId={(row: TranscriptSectionIndexDB) => row.transcriptSectionId}
                    rows={incompleteUploads}
                    columns={columns}
                    editMode="row"
                    checkboxSelection={false}
                    hideFooter={true}
                    getRowClassName={() => "datagrid-table--row"}
                    getCellClassName={() => "datagrid-table--cell"}
                    sx={getDataGridStyles(false)}
                />
            </div>
        </>
    );
};

export default IncompleteUploads;
